import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import axios, { CancelTokenSource } from 'axios';
import debounce from 'lodash.debounce';
import moment from 'moment';
import FileSaver from 'file-saver';
import { toast } from 'react-toastify';

import InfoTooltip from '../../../acquisition/components/InfoTooltip';
import { listStores, Store } from '../../../plans';
import {
  DEFAULT_RANGE_MONTHS,
  HISTORY_START_MONTH,
  MAX_RANGE_MONTHS,
  QUICK_RANGES_MONTHS,
} from '../../constants/subscriberCohorts';
import {
  RETENTION_MODES,
  RetentionMode,
  SubscriberCohortsReport,
  addMonths,
  monthKey,
  monthsBetween,
} from '../../models/SubscriberCohortsModel';
import {
  SubscriberCohortsParams,
  downloadSubscriberCohortsExcel,
  getSubscriberCohorts,
  subscriberCohortsFilename,
} from '../../services/SubscriberCohortsCRUD';
import CohortChurnReasonsChart from './CohortChurnReasonsChart';
import CohortCurveChart from './CohortCurveChart';
import CohortHeatmapTable from './CohortHeatmapTable';
import CohortKpiTiles from './CohortKpiTiles';
import CohortStatusChart from './CohortStatusChart';

const DEBOUNCE_MS = 500;
const MONTH_INPUT_WIDTH_PX = 170;
const SELECT_MIN_WIDTH_PX = 190;
const INFO_TOOLTIP_WIDTH_PX = 360;
// While a new range loads, the previous one stays on screen, dimmed.
const REFETCH_OPACITY = 0.5;

// i18n ids below are relative to `subscriberCohorts.`.
const RULE_LABEL_ID: Record<RetentionMode, string> = {
  orders: 'ruleOrders',
  coverage: 'ruleCoverage',
};
const RULE_HELP_ID: Record<RetentionMode, string> = {
  orders: 'ruleOrdersHelp',
  coverage: 'ruleCoverageHelp',
};

function rangeErrorId(
  since: string,
  until: string,
  currentMonth: string
): string | null {
  if (!since || !until) return 'rangeMissing';
  if (monthsBetween(since, until) < 0) return 'rangeReversed';
  if (monthsBetween(until, currentMonth) < 0) return 'rangeFuture';
  if (monthsBetween(since, until) >= MAX_RANGE_MONTHS) return 'rangeTooLong';
  return null;
}

// Cohorts of the Plan model for the business KPIs: a plan joins the cohort of
// the month of its first paid order and is followed month by month. The two
// retention rules come in the same response, so switching between them is
// instant; the XLSX is built by the backend with the rule selected here.
const SubscriberCohorts: FC = () => {
  const intl = useIntl();
  const t = (id: string, values?: Record<string, string | number>) =>
    intl.formatMessage({ id: `subscriberCohorts.${id}` }, values);

  const currentMonth = moment().format('YYYY-MM');
  const [since, setSince] = useState(
    addMonths(currentMonth, 1 - DEFAULT_RANGE_MONTHS)
  );
  const [until, setUntil] = useState(currentMonth);
  const [storeId, setStoreId] = useState<number | null>(null);
  const [stores, setStores] = useState<Store[]>([]);
  const [mode, setMode] = useState<RetentionMode>('orders');
  const [highlighted, setHighlighted] = useState<string | null>(null);
  const [report, setReport] = useState<SubscriberCohortsReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);
  const cancelRef = useRef<CancelTokenSource | null>(null);

  const rangeError = rangeErrorId(since, until, currentMonth);

  useEffect(() => {
    listStores()
      .then(({ data }) => setStores(data))
      // Stores not loaded: the dropdown keeps only "all stores".
      .catch(() => undefined);
  }, []);

  const debouncedFetch = useMemo(
    () =>
      debounce(async (params: SubscriberCohortsParams) => {
        // A slow earlier response must not overwrite a newer one. axios 0.21
        // predates AbortController, hence the CancelToken.
        cancelRef.current?.cancel();
        const source = axios.CancelToken.source();
        cancelRef.current = source;

        setLoading(true);
        try {
          const data = await getSubscriberCohorts(params, source.token);
          if (cancelRef.current !== source) return;
          setReport(data);
          setError(null);
        } catch (err: any) {
          if (cancelRef.current !== source || axios.isCancel(err)) return;
          const status = err?.response?.status;
          // The statements run under a server-side timeout; when it trips
          // Django answers 500.
          const isTimeout =
            err?.code === 'ECONNABORTED' ||
            status === 408 ||
            status === 500 ||
            status === 504;
          setError(
            intl.formatMessage({
              id: `subscriberCohorts.${isTimeout ? 'timeoutError' : 'fetchError'}`,
            })
          );
        } finally {
          if (cancelRef.current === source) setLoading(false);
        }
      }, DEBOUNCE_MS),
    [intl]
  );

  useEffect(() => {
    if (rangeError) {
      debouncedFetch.cancel();
      return;
    }
    debouncedFetch({ since, until, storeId });
    return () => debouncedFetch.cancel();
  }, [since, until, storeId, rangeError, debouncedFetch]);

  useEffect(
    () => () => {
      cancelRef.current?.cancel();
      cancelRef.current = null;
    },
    []
  );

  // A highlighted cohort that falls out of the new range is dropped.
  const cohortMonths = report?.cohorts.map((c) => monthKey(c.month)) ?? [];
  const highlightedMonth =
    highlighted && cohortMonths.includes(highlighted) ? highlighted : null;

  function selectQuickRange(months: number | null) {
    setUntil(currentMonth);
    setSince(
      months === null
        ? HISTORY_START_MONTH
        : addMonths(currentMonth, 1 - months)
    );
  }

  async function handleDownload() {
    setDownloading(true);
    const params = { since, until, storeId };
    try {
      const { data } = await downloadSubscriberCohortsExcel({
        ...params,
        retentionMode: mode,
      });
      FileSaver.saveAs(data, subscriberCohortsFilename(params));
    } catch {
      toast.error(t('downloadError'));
    } finally {
      setDownloading(false);
    }
  }

  const hasSubscribers = (report?.kpis.subscribers ?? 0) > 0;

  return (
    <div>
      <div className="d-flex align-items-center mb-2">
        <h3 className="fs-3 fw-bolder mb-0">{t('title')}</h3>
        <InfoTooltip widthPx={INFO_TOOLTIP_WIDTH_PX}>
          <div className="mb-2">{t('definitionCohort')}</div>
          <div className="mb-2">{t('definitionPaidOrder')}</div>
          <div className="mb-2">{t('definitionRevenue')}</div>
          <div>{t('definitionAverage')}</div>
        </InfoTooltip>
      </div>
      <p className="text-muted fs-7 mb-6">{t('description')}</p>

      <div className="d-flex flex-wrap align-items-end gap-6 mb-8">
        <div style={{ width: `${MONTH_INPUT_WIDTH_PX}px` }}>
          <label className="form-label" htmlFor="cohorts-since">
            {t('since')}
          </label>
          <input
            id="cohorts-since"
            type="month"
            className={`form-control ${rangeError ? 'is-invalid' : ''}`}
            value={since}
            min={HISTORY_START_MONTH}
            max={currentMonth}
            onChange={(e) => setSince(e.target.value)}
          />
        </div>
        <div style={{ width: `${MONTH_INPUT_WIDTH_PX}px` }}>
          <label className="form-label" htmlFor="cohorts-until">
            {t('until')}
          </label>
          <input
            id="cohorts-until"
            type="month"
            className={`form-control ${rangeError ? 'is-invalid' : ''}`}
            value={until}
            min={HISTORY_START_MONTH}
            max={currentMonth}
            onChange={(e) => setUntil(e.target.value)}
          />
        </div>
        <div className="btn-group" role="group">
          {QUICK_RANGES_MONTHS.map((months) => (
            <button
              key={months}
              type="button"
              className="btn btn-sm btn-light"
              onClick={() => selectQuickRange(months)}
            >
              {t('lastMonths', { months })}
            </button>
          ))}
          <button
            type="button"
            className="btn btn-sm btn-light"
            onClick={() => selectQuickRange(null)}
          >
            {t('wholeHistory')}
          </button>
        </div>

        <div style={{ minWidth: `${SELECT_MIN_WIDTH_PX}px` }}>
          <label className="form-label" htmlFor="cohorts-store">
            {t('store')}
          </label>
          <select
            id="cohorts-store"
            className="form-select"
            value={storeId ?? ''}
            onChange={(e) =>
              setStoreId(e.target.value === '' ? null : Number(e.target.value))
            }
          >
            <option value="">{t('allStores')}</option>
            {stores.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="form-label d-flex align-items-center">
            {t('retentionRule')}
            <InfoTooltip widthPx={INFO_TOOLTIP_WIDTH_PX}>
              {RETENTION_MODES.map((ruleMode) => (
                <div key={ruleMode} className="mb-2">
                  <strong>{t(RULE_LABEL_ID[ruleMode])}:</strong>{' '}
                  {t(RULE_HELP_ID[ruleMode])}
                </div>
              ))}
            </InfoTooltip>
          </label>
          <div className="btn-group d-flex" role="group">
            {RETENTION_MODES.map((ruleMode) => (
              <button
                key={ruleMode}
                type="button"
                className={`btn btn-sm ${
                  mode === ruleMode ? 'btn-primary' : 'btn-light'
                }`}
                aria-pressed={mode === ruleMode}
                onClick={() => setMode(ruleMode)}
              >
                {t(RULE_LABEL_ID[ruleMode])}
              </button>
            ))}
          </div>
        </div>

        <div style={{ minWidth: `${SELECT_MIN_WIDTH_PX}px` }}>
          <label className="form-label" htmlFor="cohorts-highlight">
            {t('highlight')}
          </label>
          <select
            id="cohorts-highlight"
            className="form-select"
            value={highlightedMonth ?? ''}
            onChange={(e) => setHighlighted(e.target.value || null)}
            disabled={!hasSubscribers}
          >
            <option value="">{t('noHighlight')}</option>
            {report?.cohorts
              .filter((c) => c.size > 0)
              .map((c) => (
                <option key={c.month} value={monthKey(c.month)}>
                  {monthKey(c.month)}
                </option>
              ))}
          </select>
        </div>

        {loading && (
          <span
            className="spinner-border spinner-border-sm text-primary mb-3"
            role="status"
            aria-label={t('loading')}
          />
        )}

        <div className="ms-auto d-flex align-items-center">
          <button
            type="button"
            className="btn btn-sm btn-primary"
            onClick={handleDownload}
            disabled={!!rangeError || downloading}
          >
            {downloading ? (
              <span className="spinner-border spinner-border-sm me-2" />
            ) : (
              <i className="fas fa-file-excel me-2" />
            )}
            {downloading ? t('downloading') : t('download')}
          </button>
          <InfoTooltip
            text={t('downloadHelp')}
            widthPx={INFO_TOOLTIP_WIDTH_PX}
          />
        </div>
      </div>

      {rangeError && (
        <div className="text-danger fs-7 mb-6">
          {t(rangeError, { months: MAX_RANGE_MONTHS })}
        </div>
      )}
      {error && <div className="alert alert-danger">{error}</div>}
      {!report && loading && (
        <div className="d-flex align-items-center gap-3 text-muted py-10">
          <span className="spinner-border text-primary" />
          {t('loading')}
        </div>
      )}

      {report && (
        <div
          style={{ opacity: loading ? REFETCH_OPACITY : 1 }}
          aria-busy={loading}
        >
          {!hasSubscribers ? (
            <p className="text-muted">{t('noData')}</p>
          ) : (
            <>
              <CohortKpiTiles kpis={report.kpis} mode={mode} />
              <div className="row g-8 mb-8">
                <div className="col-xl-6">
                  <CohortCurveChart
                    report={report}
                    metric="retention"
                    mode={mode}
                    highlighted={highlightedMonth}
                  />
                </div>
                <div className="col-xl-6">
                  <CohortCurveChart
                    report={report}
                    metric="ltv"
                    mode={mode}
                    highlighted={highlightedMonth}
                  />
                </div>
              </div>
              <div className="mb-8">
                <CohortHeatmapTable
                  report={report}
                  mode={mode}
                  highlighted={highlightedMonth}
                />
              </div>
              <div className="row g-8">
                <div className="col-xl-7">
                  <CohortStatusChart cohorts={report.cohorts} />
                </div>
                <div className="col-xl-5">
                  <CohortChurnReasonsChart reasons={report.churn_reasons} />
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default SubscriberCohorts;
